import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  X, Users, Clock, MapPin, User, Ban, Trash2, Check, UserX, UserPlus,
  Loader2, Search, Pencil, Save,
} from 'lucide-react'
import { format, parseISO, isValid } from 'date-fns'
import toast from 'react-hot-toast'
import { classes, members as membersApi } from '../api/endpoints.js'

const toDate = (v) => {
  if (!v) return null
  const d = typeof v === 'string' ? parseISO(v) : new Date(v)
  return isValid(d) ? d : null
}

const nameOf = (m) =>
  m?.full_name || `${m?.first_name ?? ''} ${m?.last_name ?? ''}`.trim() || m?.email || m?.id || '—'

function BookingRow({ b, classId, onBusy, busy }) {
  const qc = useQueryClient()
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['class-attendees', classId] })
    qc.invalidateQueries({ queryKey: ['classes'] })
  }

  const act = useMutation({
    mutationFn: ({ kind }) => {
      if (kind === 'checkin') return classes.checkInBooking(classId, b.id)
      if (kind === 'noshow') return classes.markNoShow(classId, b.id)
      if (kind === 'cancel') return classes.cancelBooking(classId, b.id)
      throw new Error('unknown')
    },
    onMutate: () => onBusy(b.id),
    onSuccess: (_r, { kind }) => {
      toast.success(kind === 'checkin' ? 'Checked in' : kind === 'noshow' ? 'Marked no-show' : 'Booking cancelled')
      invalidate()
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Action failed'),
    onSettled: () => onBusy(null),
  })

  const waitlisted = b.status === 'waitlisted' || b.waitlist_position != null
  const done = ['attended', 'checked_in', 'no_show', 'cancelled'].includes(b.status)

  const badge =
    b.status === 'attended' || b.status === 'checked_in' ? 'badge-active'
    : b.status === 'no_show' ? 'badge-suspended'
    : b.status === 'cancelled' ? 'badge-cancelled'
    : waitlisted ? 'badge-alert' : 'badge-info'

  return (
    <div className="flex items-center gap-2 py-2 border-b border-line last:border-0">
      <div className="grid place-items-center w-7 h-7 rounded-full bg-primary/15 text-primary text-[10px] font-bold shrink-0">
        {nameOf(b.member).split(/\s+/).slice(0, 2).map((x) => x[0]).join('').toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-text-primary truncate">{nameOf(b.member)}</div>
        <div className="text-[11px] text-text-dim">
          {waitlisted ? `Waitlist #${b.waitlist_position ?? '?'}` : (b.status ?? 'booked').replace('_', ' ')}
        </div>
      </div>
      <span className={`${badge} shrink-0`}>{(b.status ?? 'booked').replace('_', ' ')}</span>
      {!done && (
        <div className="flex items-center gap-0.5 shrink-0">
          <button title="Check in" aria-label="Check in" disabled={busy}
                  onClick={() => act.mutate({ kind: 'checkin' })}
                  className="p-1.5 rounded-md text-text-dim hover:text-[rgb(var(--ok))] hover:bg-[rgb(var(--ok))]/12 transition-all active:scale-95 disabled:opacity-40">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          </button>
          <button title="No-show" aria-label="No-show" disabled={busy}
                  onClick={() => act.mutate({ kind: 'noshow' })}
                  className="p-1.5 rounded-md text-text-dim hover:text-[rgb(var(--warn))] hover:bg-[rgb(var(--warn))]/12 transition-all active:scale-95 disabled:opacity-40">
            <UserX className="w-3.5 h-3.5" />
          </button>
          <button title="Cancel booking" aria-label="Cancel booking" disabled={busy}
                  onClick={() => act.mutate({ kind: 'cancel' })}
                  className="p-1.5 rounded-md text-text-dim hover:text-[rgb(var(--danger))] hover:bg-[rgb(var(--danger))]/12 transition-all active:scale-95 disabled:opacity-40">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  )
}

function BookMember({ classId, onDone }) {
  const qc = useQueryClient()
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)

  const { data } = useQuery({
    queryKey: ['members', 'search', q],
    queryFn: () => membersApi.list({ q, page: 1, per_page: 8 }).then((r) => r.data),
    enabled: open && q.length >= 2,
  })
  const results = Array.isArray(data) ? data : data?.items ?? []

  const book = useMutation({
    mutationFn: (memberId) => classes.book(classId, { member_id: memberId }),
    onSuccess: (r) => {
      const wl = r?.data?.waitlist_position
      toast.success(wl ? `Added to waitlist (#${wl})` : 'Member booked')
      qc.invalidateQueries({ queryKey: ['class-attendees', classId] })
      qc.invalidateQueries({ queryKey: ['classes'] })
      setQ(''); setOpen(false); onDone?.()
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not book member'),
  })

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-dim pointer-events-none" />
        <input
          className="input-field !pl-8"
          placeholder="Search a member to book…"
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true) }}
        />
      </div>
      {open && q.length >= 2 && (
        <div className="absolute z-20 left-0 right-0 mt-1 card !p-1 max-h-56 overflow-y-auto">
          {results.length === 0 ? (
            <div className="px-3 py-2 text-sm text-text-dim">No matches</div>
          ) : results.map((m) => (
            <button key={m.id} type="button" disabled={book.isPending}
                    onClick={() => book.mutate(m.id)}
                    className="w-full text-left px-3 py-2 rounded-md text-sm hover:bg-surface-3 transition-colors flex items-center gap-2 disabled:opacity-50">
              <UserPlus className="w-3.5 h-3.5 text-text-dim shrink-0" />
              <span className="truncate">{nameOf(m)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default function ClassDrawer({ cls, onClose, colorFor }) {
  const qc = useQueryClient()
  const [busyId, setBusyId] = useState(null)
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState(null)

  const { data: attendees, isLoading } = useQuery({
    queryKey: ['class-attendees', cls?.id],
    queryFn: () => classes.getAttendees(cls.id).then((r) => r.data),
    enabled: !!cls?.id,
  })
  const bookings = Array.isArray(attendees) ? attendees : attendees?.items ?? []
  const confirmed = bookings.filter((b) => !['cancelled'].includes(b.status) && b.waitlist_position == null)
  const waitlist = bookings.filter((b) => b.waitlist_position != null)

  const startEdit = () => {
    const s = toDate(cls.start_time)
    setForm({
      name: cls.name ?? '',
      location: cls.location ?? '',
      capacity: cls.capacity ?? '',
      date: s ? format(s, 'yyyy-MM-dd') : '',
      time: s ? format(s, 'HH:mm') : '',
      duration_minutes: cls.duration_minutes ?? 60,
    })
    setEditing(true)
  }

  const save = useMutation({
    mutationFn: () => {
      const start = new Date(`${form.date}T${form.time}`)
      const end = new Date(start.getTime() + Number(form.duration_minutes) * 60000)
      return classes.update(cls.id, {
        name: form.name,
        location: form.location || null,
        capacity: form.capacity ? Number(form.capacity) : null,
        start_time: start.toISOString(),
        end_time: end.toISOString(),
        duration_minutes: Number(form.duration_minutes),
      })
    },
    onSuccess: () => {
      toast.success('Class updated')
      qc.invalidateQueries({ queryKey: ['classes'] })
      setEditing(false); onClose()
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not update class'),
  })

  const remove = useMutation({
    mutationFn: () => classes.remove(cls.id),
    onSuccess: () => {
      toast.success('Class deleted')
      qc.invalidateQueries({ queryKey: ['classes'] })
      onClose()
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not delete class'),
  })

  if (!cls) return null
  const start = toDate(cls.start_time)
  const end = toDate(cls.end_time)

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm animate-fade-in p-4"
         onClick={onClose}>
      <div className="card w-full max-w-lg animate-pop max-h-[88vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>

        <div className="flex items-start justify-between mb-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: colorFor(cls) }} />
              {editing
                ? <input className="input-field !py-1" value={form.name}
                         onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                : <h3 className="text-lg font-bold truncate">{cls.name}</h3>}
            </div>
            {!editing && (
              <p className="text-sm text-text-muted mt-0.5">
                {(cls.category ?? '').toString().replace('_', ' ')}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {!editing && (
              <button onClick={startEdit} className="btn-ghost p-1.5" title="Edit class" aria-label="Edit class">
                <Pencil className="w-4 h-4" />
              </button>
            )}
            <button onClick={onClose} className="btn-ghost p-1.5" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {cls.is_cancelled && (
          <div className="badge-suspended mb-3"><Ban className="w-3 h-3" /> Cancelled</div>
        )}

        {editing ? (
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Date</label>
              <input type="date" className="input-field" value={form.date}
                     onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} /></div>
            <div><label className="label">Start</label>
              <input type="time" className="input-field" value={form.time}
                     onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} /></div>
            <div><label className="label">Duration (min)</label>
              <input type="number" min="15" step="15" className="input-field" value={form.duration_minutes}
                     onChange={(e) => setForm((f) => ({ ...f, duration_minutes: e.target.value }))} /></div>
            <div><label className="label">Capacity</label>
              <input type="number" min="1" className="input-field" value={form.capacity}
                     onChange={(e) => setForm((f) => ({ ...f, capacity: e.target.value }))} /></div>
            <div className="col-span-2"><label className="label">Location</label>
              <input className="input-field" value={form.location}
                     onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} /></div>
            <div className="col-span-2 flex justify-between gap-2 mt-1">
              <button className="btn-danger inline-flex items-center gap-2"
                      disabled={remove.isPending}
                      onClick={() => { if (window.confirm('Delete this class? Bookings will be lost.')) remove.mutate() }}>
                <Trash2 className="w-4 h-4" /> Delete
              </button>
              <div className="flex gap-2">
                <button className="btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
                <button className="btn-primary inline-flex items-center gap-2" disabled={save.isPending}
                        onClick={() => save.mutate()}>
                  <Save className="w-4 h-4" />{save.isPending ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <dl className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-text-muted">
                <Clock className="w-4 h-4 shrink-0" />
                {start && format(start, 'EEE d MMM, h:mm a')}{end && ` – ${format(end, 'h:mm a')}`}
              </div>
              {cls.coach_name && (
                <div className="flex items-center gap-2 text-text-muted">
                  <User className="w-4 h-4 shrink-0" />{cls.coach_name}
                </div>
              )}
              {cls.location && (
                <div className="flex items-center gap-2 text-text-muted">
                  <MapPin className="w-4 h-4 shrink-0" />{cls.location}
                </div>
              )}
              <div className="flex items-center gap-2 text-text-muted">
                <Users className="w-4 h-4 shrink-0" />
                {cls.capacity != null
                  ? `${confirmed.length} of ${cls.capacity} booked`
                  : `${confirmed.length} booked`}
                {waitlist.length ? ` · ${waitlist.length} on waitlist` : ''}
              </div>
            </dl>

            {cls.description && (
              <p className="text-sm text-text-muted mt-3 pt-3 border-t border-line">{cls.description}</p>
            )}

            <div className="mt-4 pt-4 border-t border-line">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-text-muted mb-2">
                Roster
              </h4>
              <BookMember classId={cls.id} />
              <div className="mt-2">
                {isLoading ? (
                  <div className="space-y-2 py-2">
                    {Array.from({ length: 3 }).map((_, i) => <div key={i} className="skeleton h-9" />)}
                  </div>
                ) : bookings.length === 0 ? (
                  <p className="text-sm text-text-dim py-4 text-center">Nobody booked yet.</p>
                ) : (
                  bookings.map((b) => (
                    <BookingRow key={b.id} b={b} classId={cls.id}
                                busy={busyId === b.id} onBusy={setBusyId} />
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
